import React, { useEffect, useRef, useState, forwardRef } from 'react';

const CustomVideoBox = forwardRef(({ onChange, videoUrl, label, name, maxSize, hint, ...props }, ref) => {
    const [videoSrc, setVideoSrc] = useState(videoUrl);
    const [type, setType] = useState('');
    const [sizeError, setSizeError] = useState('');
    const inputId = `file-input-${name || 'video'}`;
    const inputRef = useRef(null);

    useEffect(() => {
        setVideoSrc(videoUrl);
        setType(/\.(jpeg|jpg|png|gif|webp)$/i.test(videoUrl));
    }, [videoUrl]);

    const handleVideoChange = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            if (maxSize && file.size > maxSize) {
                const limitLabel = maxSize >= 1024 * 1024
                    ? `${(maxSize / (1024 * 1024)).toFixed(0)} MB`
                    : `${Math.round(maxSize / 1024)} KB`;
                setSizeError(`File is too large. Maximum allowed size is ${limitLabel}.`);
                onChange(event, '', '');
                event.target.value = '';
                setVideoSrc(videoUrl);
                setType(true);
                return;
            }
            setSizeError('');
            const objectUrl = URL.createObjectURL(file);
            setVideoSrc(objectUrl);
            setType(false);
            onChange(event, file, objectUrl);
        } else {
            setSizeError('');
            onChange(event, '', '');
            setType(true);
            setVideoSrc(videoUrl);
        }
    };

    const isHls = videoSrc && /\.m3u8($|\?)/i.test(videoSrc);

    return (
        <div {...props} ref={ref}>
            <label className="form-label">{label}</label>
            <div className="position-relative">
                <input
                    ref={inputRef}
                    name={name}
                    id={inputId}
                    type="file"
                    accept="video/mp4, video/webm, video/ogg, video/avi, video/quicktime, video/x-msvideo, video/x-matroska"
                    onChange={handleVideoChange}
                    style={{ display: 'none' }}
                />
                {isHls ? (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: '8px',
                        marginTop: '10px',
                        height: '120px',
                        color: 'var(--body-text-color, #aaa)',
                        fontSize: '13px',
                        gap: '8px',
                    }}>
                        <i className="fas fa-check-circle" style={{ fontSize: '24px', color: '#4caf50' }} />
                        <span>Video already uploaded</span>
                    </div>
                ) : type ? (
                    <img
                        className="custom_img img-fluid actor_profile modal_placeholder_image"
                        src={videoSrc}
                        alt="Preview"
                    />
                ) : (
                    <video
                        key={videoSrc}
                        className="custom_img img-fluid actor_profile modal_placeholder_image"
                        src={videoSrc}
                        controls
                        preload="metadata"
                        width="100%"
                        style={{ borderRadius: '8px', marginTop: '10px', display: 'block' }}
                    />
                )}
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    style={{
                        marginTop: '8px',
                        padding: '6px 14px',
                        fontSize: '13px',
                        cursor: 'pointer',
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.18)',
                        borderRadius: '6px',
                        color: 'var(--body-text-color, #ccc)',
                        width: '100%',
                    }}
                >
                    Change Video
                </button>
            </div>
            {sizeError ? (
                <small style={{ color: '#f82541', fontSize: '11px', marginTop: '5px', display: 'block' }}>
                    ⚠ {sizeError}
                </small>
            ) : hint ? (
                <small style={{ color: 'rgba(255,255,255,0.38)', fontSize: '11px', marginTop: '5px', display: 'block' }}>
                    {hint}
                </small>
            ) : null}
        </div>
    );
});

CustomVideoBox.displayName = 'CustomVideoBox';

export default CustomVideoBox;
